import { getLatestWorkpost, getMinimumWorkStartDate, getWorkPostLevelOptions } from './workDetailsLogic';
import { portalAction, portalScreenContext } from '../../../app/telemetry/portalTelemetry';
import { usePortalScreen } from '../../../app/telemetry/usePortalTelemetry';
import { Box, Grid, Button, Paper } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { useEffect, useState, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import AppAutocomplete from "../../../app/components/AppAutocomplete";
import AppDatePicker from "../../../app/components/AppDatePicker";
import AppTextInput from "../../../app/components/AppTextInput";
import { useGetRegistrationDetails } from "../../../app/customHooks/useGetRegistrationDetails";
import { useGetWorkpostList } from "../../../app/customHooks/useGetWorkpostList";
import { ApiRequestStatus } from "../../../app/enums/apiRequestStatus";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import { IdTokenClaims } from "../../../app/models/account/idTokenClaims";
import registrationFormModel from "../../../app/models/pcsEnrolment/registrationFormModel";
import { WorkPostDto } from "../../../app/models/workpost/workPostDto";
import { useAppSelector } from "../../../app/store/configureStore";
import { MESSAGE_PROCESSING_DETAILS } from "../../../app/utils/constant";
import { MapCountriesToAutocompleteItems, MapHospitalsToAutocompleteItems } from "../../../app/utils/convertStaticDataToAutocompleteItems";
import { pcsFilter } from "../../../app/utils/util";
import { WORK_START_DATE_MIN } from "../../../app/models/pcsEnrolment/registrationValidationSchema";

interface Props {
  backToWorkDetailsClick?: () => void;
  handleSave?: () => void;
}

export default function AddWorkDetails({ backToWorkDetailsClick, handleSave }: Props) {
  const methods = useFormContext();
  const { control, getValues, watch, reset, setValue } = methods;

  const { countries, workPostLevels, hospitals } = useAppSelector(state => state.ReferenceData);
  const { registrationDetails } = useAppSelector(state => state.pcs);
  const { workpostList, workpostListStatus, workpostStatus, saveWorkpostStatus } = useAppSelector(state => state.workpost);

  const [showOtherNameOfEmployer, setShowOtherNameOfEmployer] = useState<boolean>(false);
  const [latestWorkpost, setLatestWorkpost] = useState<WorkPostDto>();

  const {
    workDetailsFormField: {
      StartDate,
      PostLevel,
      Country,
      NameOfClinicalSupervisor,
      NameOfEmployer,
      OtherNameOfEmployer,
      JobTitle,
      CurrentUserRoles
    }
  } = registrationFormModel;

  const { instance } = useMsal();
  const roleClaim = (instance.getActiveAccount()?.idTokenClaims as unknown as IdTokenClaims | undefined)?.extension_portalcrm_userType;
  const userRoles = useMemo(() => roleClaim?.split(',') ?? [], [roleClaim]);

  useGetWorkpostList();
  useGetRegistrationDetails();

  useEffect(() => {
    if (workpostList && workpostList.length > 0) {
      setLatestWorkpost(getLatestWorkpost(workpostList));
    } else {
      setLatestWorkpost(undefined);
    }
  }, [workpostList]);

  useEffect(() => {
    reset({
      [StartDate.name]: null,
      [PostLevel.name]: '',
      [Country.name]: '',
      [NameOfClinicalSupervisor.name]: '',
      [NameOfEmployer.name]: '',
      [OtherNameOfEmployer.name]: '',
      [JobTitle.name]: '',
      [CurrentUserRoles.name]: userRoles
    });
  }, [reset, userRoles, StartDate.name, PostLevel.name, Country.name, NameOfClinicalSupervisor.name,
    NameOfEmployer.name, OtherNameOfEmployer.name, JobTitle.name, CurrentUserRoles.name]);

  useEffect(() => {
    const minimumStartDate = getMinimumWorkStartDate(latestWorkpost);
    setValue(WORK_START_DATE_MIN, minimumStartDate, { shouldValidate: true });
  }, [latestWorkpost, setValue]);

  useEffect(() => {
    const subscription = watch((_, { name, type }) => {
      if (name === NameOfEmployer.name && type === 'change') {
        if (getValues(NameOfEmployer.name) === '') {
          setShowOtherNameOfEmployer(true);
        } else {
          setShowOtherNameOfEmployer(false);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [NameOfEmployer.name, watch, getValues]);

  const isWorkpostRequestPending = workpostStatus === ApiRequestStatus.Pending
    || saveWorkpostStatus === ApiRequestStatus.Pending
    || workpostListStatus === ApiRequestStatus.Pending;

  usePortalScreen(portalScreenContext('AddWorkDetails'), undefined, !isWorkpostRequestPending);

  if (isWorkpostRequestPending) {
    return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />
  }

  return (
    <>
      <Box sx={{ width: '100%' }} component={Paper}>
        <Grid container rowSpacing={4} sx={{ margin: "2%" }}>

          <Grid size={{ xs: 12 }}></Grid>

          <Grid size={{ xs: 12 }}>
            <AppDatePicker control={control} name={StartDate.name} label={StartDate.label}
              minDate={getMinimumWorkStartDate(latestWorkpost)}
              maxDate={new Date()} defaultValue={null} validateInvalidInput />
          </Grid>

          {userRoles && pcsFilter(userRoles) &&
            <Grid size={{ xs: 12 }}>
              <AppAutocomplete control={control} name={PostLevel.name} label={PostLevel.label}
                items={getWorkPostLevelOptions(workPostLevels, registrationDetails?.engagedInPracticeOfMedicine)} information_text={PostLevel.information_text} />
            </Grid>
          }

          <Grid size={{ xs: 12 }}>
            <AppTextInput control={control} name={JobTitle.name} label={JobTitle.label} maxStringCount={JobTitle.maxStringCount} />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <AppAutocomplete control={control} name={Country.name}
              label={Country.label} items={MapCountriesToAutocompleteItems(countries ?? [])} />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <AppAutocomplete control={control} name={NameOfEmployer.name} label={NameOfEmployer.label}
              items={MapHospitalsToAutocompleteItems(hospitals?.filter(x => x.countryId === getValues([Country.name]).toString()) ?? [])} />

            {(getValues(NameOfEmployer.name) === undefined || getValues(NameOfEmployer.name) === "") &&
              <Button variant="text" style={{ fontSize: '12px', color: 'blue', textDecoration: 'underline' }}
                onClick={portalAction(portalScreenContext('AddWorkDetails'), 'AddWorkDetails.onClick.setShowOtherNameOfEmployer', () => setShowOtherNameOfEmployer(true))}>I cannot find my employer</Button>}
          </Grid>

          {showOtherNameOfEmployer === true && <Grid size={{ xs: 12 }}>
            <AppTextInput control={control} name={OtherNameOfEmployer.name} label={OtherNameOfEmployer.label}
              maxStringCount={OtherNameOfEmployer.maxStringCount} />
          </Grid>}

          {userRoles && pcsFilter(userRoles) &&
            <Grid size={{ xs: 12 }}>
              <AppTextInput control={control} name={NameOfClinicalSupervisor.name} label={NameOfClinicalSupervisor.label}
                maxStringCount={NameOfClinicalSupervisor.maxStringCount} />
            </Grid>
          }

          <Grid size={{ xs: 12, md: 4, sm: 6 }}>
            {workpostList && workpostList?.length > 0 && <Button sx={{ "& .MuiButton-startIcon": { marginRight: "0px", marginLeft: '0' } }} variant="outlined"
              onClick={portalAction(portalScreenContext('AddWorkDetails'), 'AddWorkDetails.onClick.backToWorkDetailsClick', backToWorkDetailsClick)}>Back to work details</Button>}
          </Grid>

          <Grid size={{ xs: 12, md: 8, sm: 6 }}>
            <Button sx={{ "& .MuiButton-startIcon": { marginRight: "0px", marginLeft: '0' } }} variant="contained"
              onClick={portalAction(portalScreenContext('AddWorkDetails'), 'AddWorkDetails.onClick.handleSave', handleSave)}>Save work details</Button>
          </Grid>

          <Grid size={{ xs: 12 }}></Grid>

        </Grid>
      </Box>
    </>
  );
}
